import React, {ReactNode} from "react";
import PortalDocument, {PortalDocumentInterface} from "../PortalDocument";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import MessageBox from "../../../shared/components/MessageBox";
import i18next from "i18next";
import Exception from "../../../shared/exception/Exception";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import PortalContext from "../../PortalContext";
import {MessageType} from "../../../shared/log/MessageInterface";
import {HistoryEntry, MarkupsAnnotation, OcrOutput, PortfolioSelection, SplitMode} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../../shared/translation/Translate";
import {SidebarFunctionGlobals} from "./pdf/SidebarFunctions";
import Icon from "../../../shared/components/Icon";
import Immutable from "../../../shared/immutable/Immutable";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import DocumentSettings from "../DocumentSettings";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

enum HistoryListFunction {
	UPLOAD = "DOCUMENT:UPLOAD",
	ROTATE = "TOOLBOX:ROTATE",
	DELETE_PAGE = "TOOLBOX:DELETE",
	SPLIT = "TOOLBOX:SPLIT",
	OPTIONS = "TOOLBOX:OPTIONS",
	MERGE = "TOOLBOX:MERGE",
	WATERMARK = "TOOLBOX:WATERMARK",
	ATTACHMENT = "TOOLBOX:ATTACHMENT",
	EXTRACTION = "TOOLBOX:EXTRACTION",
	IMAGES = "TOOLBOX:IMAGES",
	FORMS = "TOOLBOX:FORMS",
	ANNOTATION = "TOOLBOX:ANNOTATION",
	REDACT = "TOOLBOX:REDACT",
	REDACT_ANNOTATION = "TOOLBOX:ANNOTATION,REDACT",
	DESCRIPTION = "TOOLBOX:DESCRIPTION",
	MOVE = "TOOLBOX:MOVE",
	OUTLINE = "TOOLBOX:OUTLINE",
	PORTFOLIO = "TOOLBOX:PORTFOLIO",
	ENCRYPTION = "TOOLBOX:ENCRYPTION",
	COMPRESS = "TOOLBOX:COMPRESS",
	SCALE = "TOOLBOX:SCALE",
	TRANSCRIBE = "TOOLBOX:TRANSCRIBE",
	PDFA = "PDFA",
	OCR = "OCR",
	SIGNATURE = "SIGNATURE",
	BARCODE = "BARCODE",
	CONVERTER = "CONVERTER",
	URLCONVERTER = "URLCONVERTER"
}

interface HistoryListEntry {
	id: number;
	active: boolean;
	fileName: string;
	dateTime: string;
	operation: { func: HistoryListFunction, options: any };
}

interface HistoryListProperties extends I18nGlobals, SidebarFunctionGlobals {
	portalDocument?: Immutable<PortalDocumentInterface>;
	context: PortalContext
}

interface HistoryListState {
	entries: Array<HistoryListEntry>,
	selection?: HistoryListEntry
}

export class HistoryListDefinition extends PortalPureComponent<HistoryListProperties, HistoryListState> {
	/**
	 * Constructs a {@link HistoryList} component
	 */
	constructor(props: Readonly<HistoryListProperties>) {
		super(props);

		this.state = {
			entries: []
		};

		this.onSelect = this.onSelect.bind(this);
		this.renderIcon = this.renderIcon.bind(this);
		this.renderDateTime = this.renderDateTime.bind(this);
		this.renderDescription = this.renderDescription.bind(this);
		this.renderEmptyMessage = this.renderEmptyMessage.bind(this);
	}

	/**
	 * called on selecting a history entry
	 */
	private async onSelect(entry: HistoryListEntry): Promise<void> {
		if (entry.active || !FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (portalDocument.isLocked()) {
			return;
		}

		let documentId: string | undefined = portalDocument.getSourceDocumentId();
		if (!FunctionHelper.exists(documentId)) {
			throw new Exception('Selected Document has no id!');
		}

		let command: () => Promise<void> = async () => {
			await this.props.context.documentManager.activateHistory(documentId!, entry.id);
		}

		if (DocumentSettings.checkActiveMouseFunction(this.props)) {
			DocumentSettings.confirmResetMouseFunction(this.props, command);
			return;
		}

		await command();
	}

	private buildEntry(history?: HistoryEntry): HistoryListEntry | undefined {
		if (!FunctionHelper.exists(history)) {
			return undefined;
		}

		let operation: any = {};
		if (FunctionHelper.exists(history!.operation) && history!.operation !== "") {
			try {
				operation = JSON.parse(history!.operation!)

				if (typeof operation.options === "string" && operation.options !== "") {
					operation.options = JSON.parse(operation.options)
				}
			} catch (e) {
				// invalid operation
			}
		}

		return {
			id: history!.id!,
			active: history!.active!,
			dateTime: history!.dateTime!,
			fileName: history!.fileName!,
			operation: operation
		};
	}

	private buildEntries(document: PortalDocument): Array<HistoryListEntry> {
		let result: Array<HistoryListEntry> = [];

		for (let entry of document.getHistory()) {
			let historyEntry: HistoryListEntry | undefined = this.buildEntry(entry);
			if (FunctionHelper.exists(historyEntry)) {
				result.push(historyEntry!);
			}
		}

		return result;
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentChanged: boolean = !this.props.portalDocument!.equals(prevProps.portalDocument);
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (documentChanged || this.props.language !== prevProps.language) {
			this.setState({
				entries: this.buildEntries(portalDocument),
				selection: this.buildEntry(portalDocument.activeHistory())
			});
		}
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		this.setState({
			entries: this.buildEntries(portalDocument),
			selection: this.buildEntry(portalDocument.activeHistory())
		});
	}

	/**
	 * render message if no history entries are available
	 *
	 * @private
	 */
	private renderEmptyMessage(): ReactNode {
		return (
			<MessageBox {...this.props} type={MessageType.INFO}>
				<Translate {...this.props} data="history.empty" namespace="sidebar"/>
			</MessageBox>
		);
	}

	/**
	 * render history entry icon
	 */
	private renderIcon(entry: HistoryListEntry): ReactNode {
		let options: any = entry.operation.options;

		try {
			switch (entry.operation.func) {
				case HistoryListFunction.UPLOAD:
					return <span className="icon icon-up"/>;
				case HistoryListFunction.ROTATE:
					return <span className={
						"icon small icon-rotate-" + options.toolbox[0].rotate.degrees
					}/>;
				case HistoryListFunction.DELETE_PAGE:
					return <Icon icon={"icon-delete-page small"}/>;
				case HistoryListFunction.SPLIT:
					switch (options.toolbox[0].split.mode as SplitMode) {
						case SplitMode.List:
						case SplitMode.First:
						case SplitMode.Last:
							return <Icon icon={"icon-split small"}/>;
						case SplitMode.Burst:
						case SplitMode.Group:
						case SplitMode.Each:
						case SplitMode.Single:
							return <Icon icon={"icon-export-page small"}/>;
					}
					break;
				case HistoryListFunction.OPTIONS:
					if (FunctionHelper.exists(options.toolbox[0].options.background)) {
						return <Icon icon={"icon-background small"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].options.headerFooter)) {
						return <Icon icon={"icon-header-footer"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].options.pageTransitions)) {
						return <Icon icon={"icon-transition small"}/>;
					} else {
						return <Icon icon={"icon-annotation-star"}/>;
					}
				case HistoryListFunction.MERGE:
					return <Icon icon={"icon-merge multiple"}/>;
				case HistoryListFunction.WATERMARK:
					return <Icon icon={"icon-watermark small"}/>;
				case HistoryListFunction.ATTACHMENT:
					return <Icon icon={"icon-attachment small"}/>;
				case HistoryListFunction.PDFA:
					return <span className="icon icon-archive small"/>;
				case HistoryListFunction.OCR:
					return <span className="icon icon-ocr small"/>;
				case HistoryListFunction.SIGNATURE:
					if (FunctionHelper.exists(options.signature.clear)) {
						return <span className="icon icon-delete small"/>;
					} else {
						return <span className="icon icon-signature small"/>;
					}
				case HistoryListFunction.EXTRACTION:
					if (FunctionHelper.exists(options.toolbox[0].extraction.text)) {
						return <Icon icon={"icon-export-content small"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].extraction.info)) {
						return <Icon icon={"icon-export-info small"}/>;
					}
					break;
				case HistoryListFunction.IMAGES:
					return <span className="icon icon-export-images small"/>;
				case HistoryListFunction.FORMS:
					if (FunctionHelper.exists(options.toolbox[0].forms.flatten)) {
						return <Icon icon={"icon-flatten"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].forms.export)) {
						return <Icon icon={"icon-forms-export small"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].forms.import)) {
						return <Icon icon={"icon-forms-import small"}/>;
					}
					break;
				case HistoryListFunction.ANNOTATION:
					if (FunctionHelper.exists(options.toolbox[0].annotation.add)) {
						if (options.toolbox[0].annotation.add.text.length > 0) {
							return <Icon icon={"icon-annotation-note"}/>;
						} else if (options.toolbox[0].annotation.add.markup.length > 0) {
							switch (options.toolbox[0].annotation.add.markup[0].markupType as MarkupsAnnotation) {
								case MarkupsAnnotation.Highlight:
									return <Icon icon={"icon-annotation-mark"}/>;
								case MarkupsAnnotation.StrikeOut:
									return <Icon icon={"icon-annotation-strike"}/>;
								case MarkupsAnnotation.Underline:
									return <Icon icon={"icon-annotation-underline"}/>;
								case MarkupsAnnotation.Squiggly:
									return <Icon icon={"icon-annotation-squiggly"}/>;
							}
						} else if (options.toolbox[0].annotation.add.redact.length > 0) {
							return <Icon icon={"icon-redact-area"}/>;
						} else if (options.toolbox[0].annotation.add.line.length > 0) {
							return <Icon icon={"icon-annotation-line"}/>;
						} else if (options.toolbox[0].annotation.add.square.length > 0) {
							return <Icon icon={"icon-annotation-square"}/>;
						} else if (options.toolbox[0].annotation.add.circle.length > 0) {
							return <Icon icon={"icon-annotation-circle"}/>;
						} else if (options.toolbox[0].annotation.add.rubberstamp.length > 0) {
							return <Icon icon={"icon-annotation-stamp"}/>;
						}
					} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit)) {
						if (FunctionHelper.exists(options.toolbox[0].annotation.edit.text)) {
							return <Icon icon={"icon-annotation-note"}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.markup)) {
							switch (options.toolbox[0].annotation.edit.markup.markupType as MarkupsAnnotation) {
								case MarkupsAnnotation.Highlight:
									return <Icon icon={"icon-annotation-mark"}/>;
								case MarkupsAnnotation.StrikeOut:
									return <Icon icon={"icon-annotation-strike"}/>;
								case MarkupsAnnotation.Underline:
									return <Icon icon={"icon-annotation-underline"}/>;
								case MarkupsAnnotation.Squiggly:
									return <Icon icon={"icon-annotation-squiggly"}/>;
							}
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.redact)) {
							return <Icon icon={"icon-redact-area"}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.line)) {
							return <Icon icon={"icon-annotation-line"}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.square)) {
							return <Icon icon={"icon-annotation-square"}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.circle)) {
							return <Icon icon={"icon-annotation-circle"}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.rubberstamp)) {
							return <Icon icon={"icon-annotation-stamp"}/>;
						}
					} else if (
						FunctionHelper.exists(options.toolbox[0].annotation?.comment?.add?.replyTo) &&
						options.toolbox[0].annotation?.comment?.add?.replyTo.length > 0
					) {
						return <Icon icon={"icon-reply"}/>;
					} else if (
						FunctionHelper.exists(options.toolbox[0].annotation?.comment?.edit?.replyTo) &&
						options.toolbox[0].annotation?.comment?.edit?.replyTo.length > 0
					) {
						return <Icon icon={"icon-reply"}/>;
					} else if (
						FunctionHelper.exists(options.toolbox[0].annotation?.remove?.selection?.select) &&
						options.toolbox[0].annotation?.remove?.selection?.select.length > 0
					) {
						return <Icon icon={"icon-delete small"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].annotation.clear)) {
						return <Icon icon={"icon-flatten"}/>;
					}
					break;
				case HistoryListFunction.BARCODE:
					if (FunctionHelper.exists(options.barcode.add)) {
						return <Icon icon={"icon-barcode-create"}/>;
					} else if (FunctionHelper.exists(options.barcode.detect)) {
						return <Icon icon={"icon-barcode-detect"}/>;
					}
					break;
				case HistoryListFunction.REDACT:
				case HistoryListFunction.REDACT_ANNOTATION:
					for (let toolboxOperation of FunctionHelper.applyValue(options.toolbox, [])) {
						if (FunctionHelper.exists(toolboxOperation.redact?.blacken)) {
							return <Icon icon={"icon-redact-apply small"}/>;
						} else if (FunctionHelper.exists(toolboxOperation.redact?.sanitize)) {
							return <Icon icon={"icon-redact-sanitize small"}/>;
						}
					}
					break;
				case HistoryListFunction.DESCRIPTION:
					return <Icon icon={"icon-list"}/>;
				case HistoryListFunction.OUTLINE:
					if (FunctionHelper.exists(options.toolbox[0].outline.add)) {
						return <Icon icon={"icon-bookmark"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].outline.remove)) {
						return <Icon icon={"icon-delete small"}/>;
					}
					break;
				case HistoryListFunction.MOVE:
					return <Icon icon={"icon-page"}/>;
				case HistoryListFunction.PORTFOLIO:
					if (FunctionHelper.exists(options.toolbox[0].portfolio.add)) {
						return <Icon icon={"icon-add"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].portfolio.remove)) {
						return <Icon icon={"icon-delete small"}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].portfolio.extract)) {
						return <Icon icon={"icon-portfolio export"}/>;
					}
					break;
				case HistoryListFunction.CONVERTER:
					return <Icon icon={"icon-convert small"}/>;
				case HistoryListFunction.ENCRYPTION:
					if (!FunctionHelper.exists(options.toolbox[0].security.decrypt)) {
						return <Icon icon={"icon-locked small"}/>;
					} else {
						return <Icon icon={"icon-unlocked small"}/>;
					}
				case HistoryListFunction.URLCONVERTER:
					return <Icon icon={"icon-globe small"}/>;
				case HistoryListFunction.SCALE:
					return <Icon icon={"icon-scale small"}/>;
				case HistoryListFunction.COMPRESS:
					return <Icon icon={"icon-compress small"}/>;
				case HistoryListFunction.TRANSCRIBE:
					return <Icon icon={"icon-export-html small"}/>;
			}
		} catch (e: any) {
			// can be ignored here
		}

		return <Icon icon={"icon-help"}/>;
	}

	/**
	 * render history entry creation time
	 */
	private renderDateTime(entry: HistoryListEntry): ReactNode {
		let creationDate = new Date(entry.dateTime);
		return isNaN(creationDate.getTime()) ? '' : creationDate.toLocaleTimeString(i18next.language);
	}

	/**
	 * render history entry description
	 */
	private renderDescription(entry: HistoryListEntry): ReactNode {
		let options: any = entry.operation.options;

		try {
			switch (entry.operation.func) {
				case HistoryListFunction.UPLOAD:
					return <Translate {...this.props} data="history.functions.upload" namespace="sidebar"/>;
				case HistoryListFunction.ROTATE:
					return <Translate {...this.props}
									  data={"history.functions.rotate." + options.toolbox[0].rotate.degrees}
									  values={{pages: options.toolbox[0].rotate.pages}} namespace="sidebar"/>;
				case HistoryListFunction.DELETE_PAGE:
					return <Translate {...this.props} data="history.functions.delete_page" namespace="sidebar"
									  values={{pages: options.toolbox[0].delete.pages}}/>;
				case HistoryListFunction.SPLIT:
					switch (options.toolbox[0].split.mode as SplitMode) {
						case SplitMode.List:
							return <Translate {...this.props} data="history.functions.split.list" namespace="sidebar"
											  values={{pages: options.toolbox[0].split.pages}}/>;
						case SplitMode.First:
							return <Translate {...this.props} data="history.functions.split.first" namespace="sidebar"
											  values={{pages: options.toolbox[0].split.pages}}/>;
						case SplitMode.Last:
							return <Translate {...this.props} data="history.functions.split.last" namespace="sidebar"
											  values={{pages: options.toolbox[0].split.pages}}/>;
						case SplitMode.Burst:
							return <Translate {...this.props} data="history.functions.split.burst"
											  namespace="sidebar"/>;
						case SplitMode.Group:
							return <Translate {...this.props} data="history.functions.split.group"
											  namespace="sidebar"/>;
						case SplitMode.Each:
							return <Translate {...this.props} data="history.functions.split.each" namespace="sidebar"
											  values={{pages: options.toolbox[0].split.pages}}/>;
						case SplitMode.Single:
							return <Translate {...this.props} data="history.functions.split.single" namespace="sidebar"
											  values={{pages: options.toolbox[0].split.pages}}/>;
					}

					return <Translate {...this.props} data={"history.functions.delete_page"} namespace="sidebar"
									  values={{pages: options.toolbox[0].delete.pages}}/>;
				case HistoryListFunction.OPTIONS:
					if (FunctionHelper.exists(options.toolbox[0].options.background)) {
						if (FunctionHelper.exists(options.toolbox[0].options.background.remove)) {
							return <Translate {...this.props} data={"history.functions.background.remove"}
											  namespace="sidebar"/>;
						} else {
							return <Translate {...this.props} data={"history.functions.background.add"}
											  namespace="sidebar"/>;
						}
					} else if (FunctionHelper.exists(options.toolbox[0].options.headerFooter)) {
						if (FunctionHelper.exists(options.toolbox[0].options.headerFooter.remove)) {
							return <Translate {...this.props} data={"history.functions.headerFooter.remove"}
											  namespace="sidebar"/>;
						} else {
							return <Translate {...this.props} data={"history.functions.headerFooter.add"}
											  namespace="sidebar"/>;
						}
					} else if (FunctionHelper.exists(options.toolbox[0].options.pageTransitions)) {
						return <Translate {...this.props} data={"history.functions.pageTransitions"} namespace="sidebar"
										  values={{
											  pages: options.toolbox[0].options.pageTransitions.add[0].pageTransition.pages
										  }}/>;
					} else {
						return <Translate {...this.props} data={"history.functions.view"} namespace="sidebar"/>;
					}
				case HistoryListFunction.MERGE:
					return <Translate {...this.props} data={"history.functions.merge"} namespace="sidebar"/>;
				case HistoryListFunction.WATERMARK:
					return <Translate {...this.props} data={"history.functions.watermark"} namespace="sidebar"
									  values={{pages: options.toolbox[0].watermark.pages}}/>;
				case HistoryListFunction.ATTACHMENT:
					if (FunctionHelper.exists(options.toolbox[0].attachment.remove)) {
						return <Translate {...this.props} data={"history.functions.attachment.remove"}
										  namespace="sidebar"/>;
					} else if (FunctionHelper.exists(options.toolbox[0].attachment.extract)) {
						return <Translate {...this.props} data={"history.functions.attachment.extract"}
										  namespace="sidebar"/>;
					} else if (FunctionHelper.exists(options.toolbox[0].attachment.add)) {
						return <Translate {...this.props} data={"history.functions.attachment.add"}
										  namespace="sidebar"/>;
					}
					break;
				case HistoryListFunction.PDFA:
					if (FunctionHelper.exists(options.pdfa.analyze)) {
						return <Translate {...this.props} data="history.functions.pdfa.analyse" namespace="sidebar"
										  values={{level: options.pdfa.analyze.level}}/>;
					}

					return <Translate {...this.props} data="history.functions.pdfa.convert" namespace="sidebar"
									  values={{level: options.pdfa.convert.level}}/>;
				case HistoryListFunction.OCR:
					switch (options.ocr.outputFormat as OcrOutput) {
						case OcrOutput.Hocr:
							return <Translate {...this.props} data="history.functions.ocr.export" namespace="sidebar"
											  values={{format: "xml"}}/>;
						case OcrOutput.Text:
							return <Translate {...this.props} data="history.functions.ocr.export" namespace="sidebar"
											  values={{format: "txt"}}/>;
					}

					return <Translate {...this.props} data="history.functions.ocr.apply" namespace="sidebar"/>;
				case HistoryListFunction.SIGNATURE:
					if (FunctionHelper.exists(options.signature.clear)) {
						return <Translate {...this.props} data={"history.functions.signature.remove"}
										  namespace="sidebar"/>;
					} else {
						return <Translate {...this.props} data={"history.functions.signature.add"}
										  namespace="sidebar"/>;
					}
				case HistoryListFunction.EXTRACTION:
					if (FunctionHelper.exists(options.toolbox[0].extraction.text)) {
						return <Translate {...this.props} data={"history.functions.extraction.content"}
										  namespace="sidebar"
										  values={{pages: options.toolbox[0].extraction.text.pages}}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].extraction.info)) {
						return <Translate {...this.props} data={"history.functions.extraction.info"} namespace="sidebar"
										  values={{format: options.toolbox[0].extraction.info.fileFormat}}/>;
					}
					break;
				case HistoryListFunction.IMAGES:
					let format: string = "";

					if (FunctionHelper.exists(options.toolbox[0].image.png)) {
						format = "png";
					} else if (FunctionHelper.exists(options.toolbox[0].image.tiff)) {
						format = "tiff";
					} else if (FunctionHelper.exists(options.toolbox[0].image.jpeg)) {
						format = "jpeg";
					} else if (FunctionHelper.exists(options.toolbox[0].image.gif)) {
						format = "gif";
					} else if (FunctionHelper.exists(options.toolbox[0].image.bmp)) {
						format = "bmp";
					} else if (FunctionHelper.exists(options.toolbox[0].image.svg)) {
						format = "svg";
					}

					return <Translate {...this.props} data={"history.functions.extraction.image"} namespace="sidebar"
									  values={{pages: options.toolbox[0].image.pages, format: format}}/>;
				case HistoryListFunction.FORMS:
					if (FunctionHelper.exists(options.toolbox[0].forms.flatten)) {
						return <Translate {...this.props} data={"history.functions.forms.flatten"}
										  namespace="sidebar"/>;
					} else if (FunctionHelper.exists(options.toolbox[0].forms.export)) {
						return <Translate {...this.props} data={"history.functions.forms.export"} namespace="sidebar"
										  values={{format: options.toolbox[0].forms.export.format}}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].forms.import)) {
						return <Translate {...this.props} data={"history.functions.forms.import"} namespace="sidebar"/>;
					}
					break;
				case HistoryListFunction.ANNOTATION:
					if (FunctionHelper.exists(options.toolbox[0].annotation.add)) {
						if (options.toolbox[0].annotation.add.text.length > 0) {
							return <Translate {...this.props} data={"history.functions.annotation.note"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.add.text[0].page}}/>;
						} else if (options.toolbox[0].annotation.add.markup.length > 0) {
							switch (options.toolbox[0].annotation.add.markup[0].markupType as MarkupsAnnotation) {
								case MarkupsAnnotation.Highlight:
									return <Translate {...this.props} data={"history.functions.annotation.highlight"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.add.markup[0].page}}/>;
								case MarkupsAnnotation.StrikeOut:
									return <Translate {...this.props} data={"history.functions.annotation.strike"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.add.markup[0].page}}/>;
								case MarkupsAnnotation.Underline:
									return <Translate {...this.props} data={"history.functions.annotation.underline"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.add.markup[0].page}}/>;
								case MarkupsAnnotation.Squiggly:
									return <Translate {...this.props} data={"history.functions.annotation.squiggly"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.add.markup[0].page}}/>;
							}
						} else if (options.toolbox[0].annotation.add.redact.length > 0) {
							return <Translate {...this.props} data={"history.functions.redact.area"} namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.add.redact[0].page}}/>;
						} else if (options.toolbox[0].annotation.add.line.length > 0) {
							return <Translate {...this.props} data={"history.functions.annotation.line"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.add.line[0].page}}/>;
						} else if (options.toolbox[0].annotation.add.square.length > 0) {
							return <Translate {...this.props} data={"history.functions.annotation.square"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.add.square[0].page}}/>;
						} else if (options.toolbox[0].annotation.add.circle.length > 0) {
							return <Translate {...this.props} data={"history.functions.annotation.circle"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.add.circle[0].page}}/>;
						} else if (options.toolbox[0].annotation.add.rubberstamp.length > 0) {
							return <Translate {...this.props} data={"history.functions.annotation.stamp"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.add.rubberstamp[0].page}}/>;
						}
					} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit)) {
						if (FunctionHelper.exists(options.toolbox[0].annotation.edit.text)) {
							return <Translate {...this.props} data={"history.functions.annotation.edit.note"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.edit.text.page}}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.markup)) {
							switch (options.toolbox[0].annotation.edit.markup.markupType as MarkupsAnnotation) {
								case MarkupsAnnotation.Highlight:
									return <Translate {...this.props}
													  data={"history.functions.annotation.edit.highlight"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.edit.markup.page}}/>;
								case MarkupsAnnotation.StrikeOut:
									return <Translate {...this.props} data={"history.functions.annotation.edit.strike"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.edit.markup.page}}/>;
								case MarkupsAnnotation.Underline:
									return <Translate {...this.props}
													  data={"history.functions.annotation.edit.underline"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.edit.markup.page}}/>;
								case MarkupsAnnotation.Squiggly:
									return <Translate {...this.props}
													  data={"history.functions.annotation.edit.squiggly"}
													  namespace="sidebar"
													  values={{pages: options.toolbox[0].annotation.edit.markup.page}}/>;
							}
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.redact)) {
							return <Translate {...this.props} data={"history.functions.redact.edit"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.edit.redact.page}}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.line)) {
							return <Translate {...this.props} data={"history.functions.annotation.edit.line"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.edit.line.page}}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.square)) {
							return <Translate {...this.props} data={"history.functions.annotation.edit.square"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.edit.square.page}}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.circle)) {
							return <Translate {...this.props} data={"history.functions.annotation.edit.circle"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.edit.circle.page}}/>;
						} else if (FunctionHelper.exists(options.toolbox[0].annotation.edit.rubberstamp)) {
							return <Translate {...this.props} data={"history.functions.annotation.edit.stamp"}
											  namespace="sidebar"
											  values={{pages: options.toolbox[0].annotation.edit.rubberstamp.page}}/>;
						}
					} else if (
						FunctionHelper.exists(options.toolbox[0].annotation?.comment?.add?.replyTo) &&
						options.toolbox[0].annotation?.comment?.add?.replyTo.length > 0
					) {
						return <Translate {...this.props} data={"history.functions.annotation.reply"}
										  namespace="sidebar"/>;
					} else if (
						FunctionHelper.exists(options.toolbox[0].annotation?.comment?.edit?.replyTo) &&
						options.toolbox[0].annotation?.comment?.edit?.replyTo.length > 0
					) {
						return <Translate {...this.props} data={"history.functions.annotation.edit.reply"}
										  namespace="sidebar"/>;
					} else if (
						FunctionHelper.exists(options.toolbox[0].annotation?.remove?.selection?.select) &&
						options.toolbox[0].annotation?.remove?.selection?.select.length > 0
					) {
						return <Translate {...this.props} data={"history.functions.annotation.remove"}
										  namespace="sidebar"/>;
					} else if (FunctionHelper.exists(options.toolbox[0].annotation.clear)) {
						return <Translate {...this.props} data={"history.functions.annotation.flatten"}
										  namespace="sidebar"
										  values={{pages: options.toolbox[0].annotation.clear.pages}}/>;
					}
					break;
				case HistoryListFunction.BARCODE:
					if (FunctionHelper.exists(options.barcode.add)) {
						let format: string = "";

						if (FunctionHelper.exists(options.barcode.add.qrcode)) {
							format = "QR code";
						} else if (FunctionHelper.exists(options.barcode.add.aztec)) {
							format = "Aztec";
						} else if (FunctionHelper.exists(options.barcode.add.codabar)) {
							format = "Codabar";
						} else if (FunctionHelper.exists(options.barcode.add.code128)) {
							format = "Code 128";
						} else if (FunctionHelper.exists(options.barcode.add.code39)) {
							format = "Code 39";
						} else if (FunctionHelper.exists(options.barcode.add.datamatrix)) {
							format = "Data matrix";
						} else if (FunctionHelper.exists(options.barcode.add.ean13)) {
							format = "EAN-13";
						} else if (FunctionHelper.exists(options.barcode.add.ean8)) {
							format = "EAN-8";
						} else if (FunctionHelper.exists(options.barcode.add.itf)) {
							format = "ITF";
						} else if (FunctionHelper.exists(options.barcode.add.pdf417)) {
							format = "PDF417";
						} else if (FunctionHelper.exists(options.barcode.add.upca)) {
							format = "UPC-A";
						} else if (FunctionHelper.exists(options.barcode.add.qrswiss)) {
							format = "QR Swisscode";
						}

						return <Translate {...this.props} data={"history.functions.barcode.add"} namespace="sidebar"
										  values={{format: format}}/>;
					} else if (FunctionHelper.exists(options.barcode.detect)) {
						return <Translate {...this.props} data={"history.functions.barcode.detect"} namespace="sidebar"
										  values={{format: options.barcode.detect.outputFormat}}/>;
					}
					break;
				case HistoryListFunction.REDACT:
				case HistoryListFunction.REDACT_ANNOTATION:
					for (let toolboxOperations of FunctionHelper.applyValue(options.toolbox, [])) {
						if (FunctionHelper.exists(toolboxOperations.redact?.blacken)) {
							return <Translate {...this.props} data={"history.functions.redact.apply"}
											  namespace="sidebar"/>;
						} else if (FunctionHelper.exists(toolboxOperations.redact?.sanitize)) {
							return <Translate {...this.props} data={"history.functions.redact.sanitize"}
											  namespace="sidebar"/>;
						}
					}
					break;
				case HistoryListFunction.DESCRIPTION:
					return <Translate {...this.props} data={"history.functions.description"} namespace="sidebar"/>
				case HistoryListFunction.OUTLINE:
					if (FunctionHelper.exists(options.toolbox[0].outline.add)) {
						return <Translate {...this.props} data={"history.functions.outline.add"} namespace="sidebar"
										  values={{path: options.toolbox[0].outline.add.item[0].itemName}}/>;
					} else if (FunctionHelper.exists(options.toolbox[0].outline.remove)) {
						return <Translate {...this.props} data={"history.functions.outline.remove"} namespace="sidebar"
										  values={{path: options.toolbox[0].outline.remove.item[0].path}}/>;
					}
					break;
				case HistoryListFunction.MOVE:
					return <Translate {...this.props} data={"history.functions.move"} namespace="sidebar"/>
				case HistoryListFunction.PORTFOLIO:
					if (FunctionHelper.exists(options.toolbox[0].portfolio.add)) {
						if (options.toolbox[0].portfolio.add.file.length > 0) {
							if (options.toolbox[0].portfolio.add.file.length > 1) {
								return <Translate {...this.props} data={"history.functions.portfolio.add.multiple"}
												  namespace="sidebar"
												  values={{count: options.toolbox[0].portfolio.add.file.length}}/>
							} else {
								return <Translate {...this.props} data={"history.functions.portfolio.add.file"}
												  namespace="sidebar"
												  values={{name: options.toolbox[0].portfolio.add.file[0].fileName}}/>
							}
						} else if (options.toolbox[0].portfolio.add.folder.length > 0) {
							return <Translate {...this.props} data={"history.functions.portfolio.add.folder"}
											  namespace="sidebar"
											  values={{name: options.toolbox[0].portfolio.add.folder[0].path}}/>
						}
					} else if (FunctionHelper.exists(options.toolbox[0].portfolio.remove)) {
						let fileName: string = FunctionHelper.applyValue(
							options.toolbox[0].portfolio.remove.selection[0].fileName,
							PortfolioSelection.getFileNameDefault()
						);
						if (fileName === "") {
							fileName = options.toolbox[0].portfolio.remove.selection[0].path;
						}

						return <Translate {...this.props} data={"history.functions.portfolio.remove"}
										  namespace="sidebar"
										  values={{name: fileName}}/>
					} else if (FunctionHelper.exists(options.toolbox[0].portfolio.extract)) {
						let fileName: string = FunctionHelper.applyValue(
							options.toolbox[0].portfolio.extract.selection[0].fileName,
							PortfolioSelection.getFileNameDefault()
						);
						if (fileName === "") {
							fileName = options.toolbox[0].portfolio.extract.selection[0].path;
						}

						return <Translate {...this.props} data={"history.functions.portfolio.remove"}
										  namespace="sidebar"
										  values={{name: fileName}}/>
					}
					break;
				case HistoryListFunction.CONVERTER:
					return <Translate {...this.props} data={"history.functions.converter"} namespace="sidebar"/>
				case HistoryListFunction.ENCRYPTION:
					if (!FunctionHelper.exists(options.toolbox[0].security.decrypt)) {
						return <Translate {...this.props} data={"history.functions.encryption"} namespace="sidebar"/>
					} else {
						return <Translate {...this.props} data={"history.functions.decryption"} namespace="sidebar"/>
					}
				case HistoryListFunction.URLCONVERTER:
					return <Translate {...this.props} data={"history.functions.urlconverter"} namespace="sidebar"
									  values={{website: options.urlconverter.url}}/>;
				case HistoryListFunction.COMPRESS:
					return <Translate {...this.props} data={"history.functions.compress"} namespace="sidebar"/>;
				case HistoryListFunction.SCALE:
					return <Translate {...this.props} data={"history.functions.scale"} namespace="sidebar"
									  values={{pages: options.toolbox[0].scale.scalePages.pages}}/>;
				case HistoryListFunction.TRANSCRIBE:
					return <Translate {...this.props} data={"history.functions.transcribe"} namespace="sidebar"
									  values={{pages: options.toolbox[0].transcribe.html.pages}}/>;
			}
		} catch (e: any) {
			// can be ignored here
		}

		return <Translate {...this.props} data={"history.unknown"} namespace="sidebar"/>;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		return (
			<DataTable value={this.state.entries} selectionMode="single" selection={this.state.selection}
					   emptyMessage={this.renderEmptyMessage} responsiveLayout={"scroll"}
					   onRowSelect={(e) => this.onSelect(e.data)}>
				<Column field="active" className="column-select" selectionMode="single"/>
				<Column className="column-icon" body={this.renderIcon}/>
				<Column field="dateTime" className="column-date" body={this.renderDateTime}/>
				<Column className="column-description" body={this.renderDescription}/>
			</DataTable>
		);
	}
}

const HistoryList = React.memo(HistoryListDefinition,
	(prevProps: Readonly<HistoryListProperties>, nextProps: Readonly<HistoryListProperties>) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		return true;
	}
);

export default HistoryList;